import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { X, Plus, Trash2, ScanLine, FileText } from 'lucide-react';
import BarcodeScanner from './BarcodeScanner';
import useBarcodeScanner from '../hooks/useBarcodeScanner';
import ProjectSelect from './ProjectSelect';

const UNITS = ['pcs', 'box', 'set', 'pair', 'roll', 'kg', 'meter'];

/**
 * Shared create/edit modal for packing boxes.
 * Used by both WorkerPackingPage and AdminPackingPage so the form (description,
 * label copies, items, scanner) stays identical everywhere.
 *
 * Props: { projects, editBox, nextBoxNum, onClose, onSaved, fromChallan }
 *
 * fromChallan (optional): packing status of a Sale Challan (GET /sale-challans/:id/packing-status).
 * The new box is prefilled with every line that still has quantity left to pack,
 * and each content line stays linked to its challan line.
 */
const challanPrefill = (fc) => {
  if (!fc) return null;
  const lines = (fc.items || [])
    .map(l => ({ l, qty: Math.floor(Number(l.remaining_qty) || 0) }))
    .filter(x => x.qty > 0)
    .map(({ l, qty }) => ({ item_name: l.item_name, quantity: qty, unit: l.unit || 'pcs', sale_challan_item_id: l.id, proto_code: l.proto_code || '' }));
  const codes = [...new Set(lines.map(l => l.proto_code).filter(Boolean))];
  return { lines, photoCode: codes.length === 1 ? codes[0] : '', mainItem: lines.length === 1 ? lines[0].item_name : '' };
};

const newRequestKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`);

export default function PackingBoxModal({ projects = [], editBox, nextBoxNum, onClose, onSaved, fromChallan }) {
  const pre = !editBox ? challanPrefill(fromChallan) : null;
  const [boxNumber, setBoxNumber] = useState(editBox?.box_number || nextBoxNum || '');
  const [mainItem, setMainItem] = useState(editBox?.main_item || pre?.mainItem || '');
  const [photoCode, setPhotoCode] = useState(editBox?.photo_code || pre?.photoCode || '');
  const [selectedProject, setSelectedProject] = useState(editBox?.project_id || fromChallan?.project_id || '');
  const [notes, setNotes] = useState(editBox?.notes || '');
  const [description, setDescription] = useState(editBox?.description || '');
  const [copies, setCopies] = useState(editBox?.copies || 1);
  const [items, setItems] = useState(editBox?.items?.length > 0 ? editBox.items
    : pre?.lines.length ? pre.lines : [{ item_name: '', quantity: 1, unit: 'pcs' }]);
  const [saving, setSaving] = useState(false);
  // One key per form: a retried or double-clicked save can't create a second box
  const requestKey = useRef(newRequestKey());

  // Challan this box packs (new box from a challan, or editing a box made from one)
  const challanId = editBox ? editBox.sale_challan_id : fromChallan?.id;
  const [challanInfo, setChallanInfo] = useState(editBox ? null : fromChallan || null);
  useEffect(() => {
    if (!editBox?.sale_challan_id) return;
    api.get(`/sale-challans/${editBox.sale_challan_id}/packing-status`).then(r => setChallanInfo(r.data)).catch(() => {});
  }, [editBox?.sale_challan_id]);

  // Max per challan line in this box = what is still left (+ what this box already holds when editing)
  const limits = new Map();
  (challanInfo?.items || []).forEach(l => {
    const own = editBox ? (editBox.items || []).filter(i => Number(i.sale_challan_item_id) === l.id).reduce((s, i) => s + (Number(i.quantity) || 0), 0) : 0;
    limits.set(l.id, { max: Math.floor((Number(l.remaining_qty) || 0) + own), qty: Number(l.quantity) || 0 });
  });
  const unitOptions = [...new Set([...UNITS, ...items.map(i => i.unit).filter(Boolean)])];

  const SUB = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const copiesPreview = copies > 1
    ? Array.from({ length: Math.min(copies, 4) }, (_, i) => boxNumber + ' (' + SUB[i] + ')').join('  ') + (copies > 4 ? '  …  ' + boxNumber + ' (' + SUB[copies - 1] + ')' : '')
    : '';

  const addItem = () => setItems([...items, { item_name: '', quantity: 1, unit: 'pcs' }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => { const n = [...items]; n[i] = { ...n[i], [field]: val }; setItems(n); };

  const { scannerOpen, openScanner, closeScanner, handleScan, scanning } = useBarcodeScanner({
    onItemFound: (scannedItem) => {
      setItems(prev => {
        const hasEmptyOnly = prev.length === 1 && !prev[0].item_name.trim();
        const newItem = { item_name: scannedItem.item_name, quantity: parseInt(scannedItem.quantity) || 1, unit: scannedItem.unit || 'pcs' };
        return hasEmptyOnly ? [newItem] : [...prev, newItem];
      });
    },
  });

  const handleSave = async () => {
    if (saving) return;
    if (!boxNumber.trim()) return toast.error('Please enter a box number');
    if (items.some(it => !String(it.item_name || '').trim())) return toast.error('Please enter names for all items');
    // Same check the server makes, shown before saving
    const perLine = new Map();
    items.forEach(it => {
      if (!it.sale_challan_item_id) return;
      const id = Number(it.sale_challan_item_id);
      perLine.set(id, (perLine.get(id) || 0) + (parseInt(it.quantity) || 0));
    });
    for (const [lineId, q] of perLine) {
      const lim = limits.get(lineId);
      if (lim && q > lim.max) {
        const name = items.find(i => Number(i.sale_challan_item_id) === lineId)?.item_name;
        return toast.error(`${name}: only ${lim.max} left to pack, ${q} entered`);
      }
    }
    setSaving(true);
    try {
      const payload = { box_number: boxNumber, project_id: selectedProject || null, mode: 'manual', items, notes, description, copies, photo_code: photoCode, main_item: mainItem };
      if (editBox) {
        const res = await api.put(`/packing/boxes/${editBox.id}`, payload);
        toast.success('Box updated! ✅');
        onSaved(res.data?.box || null);
      } else {
        if (fromChallan) { payload.sale_challan_id = fromChallan.id; payload.request_key = requestKey.current; }
        const res = await api.post('/packing/boxes', payload);
        toast.success(res.data?.duplicate ? 'This box was already created ✅' : 'Box created! ✅');
        onSaved(null, res.data);
      }
    } catch (err) { toast.error(err?.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl flex flex-col max-h-[92vh]">
        <div className="modal-header flex-shrink-0 flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-gray-900">{editBox ? '✏️ Edit Box' : '📦 New Box'}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {challanId && (
            <div className="flex items-start gap-2 bg-teal-50 border border-teal-200 rounded-xl px-3 py-2 text-xs text-teal-800">
              <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                Packing for Sale Challan <b>{challanInfo?.challan_number || editBox?.sale_challan_number || ''}</b>
                {challanInfo?.client_name ? <> · {challanInfo.client_name}</> : null}
                <div className="text-teal-600 mt-0.5">Quantities can't be more than what is left to pack on the challan.</div>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Box Number</label>
            <input value={boxNumber} onChange={e => setBoxNumber(e.target.value)} placeholder="e.g. MJ-25-01"
              className="w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-3 py-2.5 text-base font-bold focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Main Item <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
              <input value={mainItem} onChange={e => setMainItem(e.target.value)} placeholder="e.g. Chair Seat"
                className="w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-3 py-2.5 text-sm focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Proto Code <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
              <input value={photoCode} onChange={e => setPhotoCode(e.target.value)} placeholder="e.g. MJ-5086"
                className="w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-3 py-2.5 text-sm focus:outline-none" />
            </div>
          </div>
          {projects.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Project <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
              <ProjectSelect projects={projects} value={selectedProject} onChange={v => setSelectedProject(v)}
              placeholder="-- Select a project --" format="name-code" className="w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-3 py-2.5 text-sm focus:outline-none" />
            </div>
          )}
          {/* Label copies — drives sub-labels (A, B, C…) for both print and export */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <label className="block text-sm font-semibold text-amber-800 mb-1.5">Label Copies <span className="text-amber-600 font-normal text-xs">(sub-labels A, B, C…)</span></label>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setCopies(c => Math.max(1, c - 1))}
                className="w-9 h-9 bg-white border border-amber-300 rounded-lg font-bold text-amber-700 hover:bg-amber-100 flex items-center justify-center text-lg">−</button>
              <input type="number" min={1} max={26} value={copies}
                onChange={e => setCopies(Math.max(1, Math.min(26, parseInt(e.target.value) || 1)))}
                className="w-16 text-center border border-amber-300 rounded-lg py-1.5 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-amber-500" />
              <button type="button" onClick={() => setCopies(c => Math.min(26, c + 1))}
                className="w-9 h-9 bg-white border border-amber-300 rounded-lg font-bold text-amber-700 hover:bg-amber-100 flex items-center justify-center text-lg">+</button>
              <span className="flex-1 text-xs text-gray-500">
                {copies > 1 ? <>Labels: <strong className="text-amber-700">{copiesPreview}</strong></> : 'Single label'}
              </span>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700">Box Contents</label>
              <div className="flex items-center gap-2">
                <button onClick={openScanner}
                  className="text-xs text-amber-600 font-semibold flex items-center gap-1 border border-amber-200 px-2 py-1 rounded-lg hover:bg-amber-50 transition-colors">
                  <ScanLine className="w-3.5 h-3.5" /> {scanning ? '...' : 'Scan'}
                </button>
                <button onClick={addItem} className="text-xs text-amber-600 font-semibold flex items-center gap-1 hover:bg-amber-50 px-2 py-1 rounded-lg transition-colors">
                  <Plus className="w-3.5 h-3.5" /> Add
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {items.map((item, i) => {
                const lim = item.sale_challan_item_id ? limits.get(Number(item.sale_challan_item_id)) : null;
                const over = lim && (parseInt(item.quantity) || 0) > lim.max;
                return (
                  <div key={i} className="bg-gray-50 rounded-xl p-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 font-bold w-4">{i+1}</span>
                      <input value={item.item_name} onChange={e => updateItem(i, 'item_name', e.target.value)} placeholder="Item name"
                        className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
                      <input type="number" min="1" max={lim ? lim.max : undefined} value={item.quantity} onChange={e => updateItem(i, 'quantity', parseInt(e.target.value)||1)}
                        className={`w-16 border rounded-lg px-2 py-1.5 text-sm text-center font-bold focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white ${over ? 'border-red-400 text-red-600' : 'border-gray-200'}`} />
                      <select value={item.unit} onChange={e => updateItem(i, 'unit', e.target.value)}
                        className="w-16 border border-gray-200 rounded-lg px-1 py-1.5 text-xs focus:outline-none bg-white">
                        {unitOptions.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                      <button onClick={() => removeItem(i)} disabled={items.length === 1} className="text-red-400 hover:text-red-600 disabled:opacity-30 p-0.5">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {item.sale_challan_item_id && (
                      <div className={`pl-6 mt-1 text-[11px] ${over ? 'text-red-600' : 'text-teal-700'}`}>
                        Challan item{item.proto_code ? <> · <span className="font-mono">{item.proto_code}</span></> : null}
                        {lim ? <> · max {lim.max} in this box (challan qty {lim.qty})</> : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description <span className="text-gray-400 font-normal text-xs">(print label par dikhega)</span></label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="e.g. SOFA — 3 seater, teak finish"
              className="w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-3 py-2 text-sm focus:outline-none resize-none" />
            {editBox
              ? <p className="text-[11px] text-gray-400 mt-1">📷 The first photo uploaded to the box will appear as the thumbnail on the print label.</p>
              : <p className="text-[11px] text-gray-400 mt-1">📷 After creating the box, upload a photo on its card — that photo will appear as the thumbnail on the print label.</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Notes <span className="text-gray-400 font-normal text-xs">(optional, internal)</span></label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Any special notes..."
              className="w-full border-2 border-gray-200 focus:border-amber-400 rounded-xl px-3 py-2 text-sm focus:outline-none resize-none" />
          </div>
        </div>
        <div className="p-4 border-t flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="btn-secondary" style={{flex:1}}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-2.5 rounded-xl text-sm font-bold disabled:opacity-50">
            {saving ? 'Save...' : editBox ? '💾 Update Box' : '📦 Create Box'}
          </button>
        </div>
      </div>
      {scannerOpen && (
        <BarcodeScanner title="Scan Box Item" onScan={handleScan} onClose={closeScanner} />
      )}
    </div>
  );
}
